import type {
	ReviewComment as ReviewCommentGql,
	ReviewNode,
	ReviewPhoto,
	ReviewsForVendor,
	StorefrontReviewSnippet,
	StorefrontReviewSnippets,
	SummaryReviews,
} from '@api/graphql/reviews/reviewsForVendor';
import type {
	Review,
	ReviewSnippet,
	ReviewSnippetsData,
	ReviewsResponse,
	ReviewsSummary,
} from 'types/api/reviews';
import type { Gallery } from 'types/reviews';

export const mapReviewsForVendor = (
	storefrontId: string,
	data: ReviewsForVendor,
	page: number,
	size: number,
): ReviewsResponse => {
	return {
		data: {
			data: mapReviews(data.reviews.nodes, storefrontId),
			resultsCount: data.reviews.nodes.length,
			totalCount: data.reviews.totalCount,
			page,
			pageSize: size,
			gallery: mapPhotos(data.reviewsPhotos.reviewsPhotos),
			reviewsSummary: mapStorefrontReviewsSummary(data.summaryReviews),
			reviewsWithPhotosCount: data.reviewsWithPhotos.totalCount,
			reviewSnippets: mapReviewSnippets(data.storefrontReviewSnippets),
		},
	};
};

export const mapReviews = (
	reviews: ReviewNode[],
	storefrontId: string,
): Review[] => {
	return reviews.map((review) => {
		return mapReview(review, storefrontId);
	});
};

const mapReview = (review: ReviewNode, storefrontId: string): Review => {
	const comments = review.comment ? mapComments([review.comment]) : [];

	return {
		id: review.id,
		profileGuid: storefrontId,
		reviewer: {
			id: review.reviewer?.memberId || '',
			userName: '',
			firstName: review.reviewer?.firstName || '',
			lastName: review.reviewer?.lastName || '',
			role: review.reviewer?.role || 'bride',
			weddingDate: review.attributes?.weddingDate || '',
			memberDate: '',
		},
		title: review.title,
		content: review.content,
		rating: review.overallRating || 0,
		createdDate: review.createdAt,
		modifiedDate: review.updatedAt,
		isFavorite: false,
		isTestimonial: review.highlighted || false,
		active: true,
		isUserVerified:
			review.userVerified === undefined ? false : review.userVerified,
		comments: comments,
		commentCount: comments.length,
		gallery: mapPhotos(review.photos),
	};
};

const mapPhotos = (photos: ReviewPhoto[]): Gallery[] => {
	if (!photos) {
		return [];
	}

	return photos.filter((photo) => photo.active).map(mapPhoto);
};

const mapPhoto = (photo: ReviewPhoto): Gallery => {
	return {
		typeCode: 'PHOTO',
		active: photo.active === undefined ? true : photo.active,
		source: {
			application: 'theknot', // This is not implemented in the new API but needed for our types
		},
		sourceId: photo.sourceId || '',
		timestamp: {
			createdAt: photo.createdAt,
			updatedAt: photo.updatedAt,
		},
		name: photo.name || '',
		width: photo.width || 0,
		height: photo.height || 0,
	};
};

const mapComments = (comments: ReviewCommentGql[]): Review['comments'] =>
	comments.map((comment) => mapComment(comment));

const mapComment = (comment: ReviewCommentGql): Review['comments'][0] => {
	return {
		id: comment.id,
		commenterId: comment.commenterId,
		commenterUserName: '',
		commenterFirstName: '',
		commenterLastName: '',
		commenterRole: '',
		text: comment.content,
		createdDate: comment.createdAt,
		modifiedDate: comment.updatedAt,
	};
};

const mapReviewSnippets = (
	snippets: StorefrontReviewSnippets | null | undefined,
): ReviewSnippetsData | null => {
	if (!snippets || !snippets.eligible || !snippets.summary) {
		return null;
	}

	return {
		snippets: snippets.snippets.map(mapReviewSnippet),
		totalReviewCount: snippets.summary.totalReviewCount,
		averageRating: snippets.summary.averageRating,
	};
};

const mapReviewSnippet = (snippet: StorefrontReviewSnippet): ReviewSnippet => ({
	reviewerName: snippet.reviewerName,
	excerpt: snippet.excerpt,
	rating: snippet.rating,
	reviewDate: snippet.reviewDate,
});

const mapStorefrontReviewsSummary = (
	summary: SummaryReviews,
): ReviewsSummary => {
	const roundToOneDecimal = (num: number) => Math.round(num * 10) / 10;

	return {
		count: summary.count,
		gallerySize: summary.gallery.photoCount,
		rating: roundToOneDecimal(summary.rating),
		ratings: {
			quality: roundToOneDecimal(summary.ratings.quality),
			professionalism: roundToOneDecimal(summary.ratings.professionalism),
			flexibility: roundToOneDecimal(summary.ratings.flexibility),
			responseTime: roundToOneDecimal(summary.ratings.responseTime),
			value: roundToOneDecimal(summary.ratings.value),
		},
		distribution: {
			stars1: summary.distribution.stars1,
			stars2: summary.distribution.stars2,
			stars3: summary.distribution.stars3,
			stars4: summary.distribution.stars4,
			stars5: summary.distribution.stars5,
		},
	};
};
